export const statusIsActive = (status) =>
  ["ACTIVE", "EXPENDED_ACTIVE", "IN_USE"].includes(String(status || "").toUpperCase());

export const getDaysBetween = (start, end) => Math.max(0, Math.round((end - start) / 86400000));

export const reuseViewLabels = {
  calendar: {
    label: "Calendar",
    helper: "Each mark = one flight on a real calendar timeline",
  },
  relative: {
    label: "Relative",
    helper: "All vehicles aligned to debut. X-axis equals days since first flight",
  },
};

const normalizePercent = (value, min, max) => {
  if (max <= min) {
    return 0;
  }

  return Math.min(96, Math.max(0, ((value - min) / (max - min)) * 94));
};

/**
 * Build a reuse-timeline row from an ordered list of flight Date objects.
 */
export const buildTimelineRowFromFlights = ({ id, name, active, flights, count }) => {
  const sortedFlights = [...(flights || [])]
    .filter((date) => date instanceof Date && !Number.isNaN(date.valueOf()))
    .sort((a, b) => a - b);

  if (!sortedFlights.length) {
    return null;
  }

  const firstFlight = sortedFlights[0];
  const lastFlight = sortedFlights[sortedFlights.length - 1];
  const span = Math.max(1, getDaysBetween(firstFlight, lastFlight));
  const flightDays = sortedFlights.map((date) => getDaysBetween(firstFlight, date));

  return {
    id,
    name,
    active: Boolean(active),
    count: Number(count || sortedFlights.length),
    dots: flightDays.map((days) => Math.min(96, Math.round((days / span) * 92))),
    flightDays,
    flightDates: sortedFlights.map((date) => date.toISOString()),
  };
};

/**
 * Parse launch times from stage_and_recoveries or spacecraft_on_launches arrays.
 */
export const extractFlightDates = (reuses) =>
  (reuses || [])
    .map((reuse) => reuse.launch?.time)
    .filter(Boolean)
    .map((time) => new Date(time))
    .filter((date) => !Number.isNaN(date.valueOf()))
    .sort((a, b) => a - b);

export const buildReuseChart = (rows, view) => {
  const safeRows = rows || [];
  const dateValues = safeRows
    .flatMap((row) => row.flightDates || [])
    .map((date) => new Date(date).valueOf())
    .filter((value) => !Number.isNaN(value));
  const minDate = Math.min(...dateValues);
  const maxDate = Math.max(...dateValues);
  const relativeDays = safeRows.flatMap((row) => row.flightDays || []);
  const maxRelativeSpan = relativeDays.length ? Math.max(0, ...relativeDays) : 0;
  const maxRelativeDay = Math.max(1, maxRelativeSpan);

  const chartRows = safeRows.map((row) => {
    const dots = (row.dots || []).map((dot, index) => {
      const flightDate = row.flightDates?.[index] ? new Date(row.flightDates[index]) : null;
      const dateLabel =
        flightDate && !Number.isNaN(flightDate.valueOf())
          ? flightDate.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
          : "date unknown";
      const daysSinceDebut = row.flightDays?.[index] ?? dot;
      const previousGap = index > 0 ? daysSinceDebut - (row.flightDays?.[index - 1] ?? 0) : 0;
      const titleParts = [`${row.name} flight ${index + 1}`, dateLabel, `${daysSinceDebut}d since debut`];

      if (index > 0) {
        titleParts.push(`${Math.max(0, previousGap)}d since previous flight`);
      }

      let x = 0;
      if (view === "calendar") {
        const value = new Date(row.flightDates?.[index]).valueOf();
        x = normalizePercent(Number.isNaN(value) ? dot : value, minDate, maxDate);
      } else {
        x = normalizePercent(daysSinceDebut, 0, maxRelativeDay);
      }

      return {
        x,
        title: titleParts.join(" • "),
      };
    });

    return { ...row, dots };
  });

  let axisLabels = ["debut"];

  if (view === "calendar" && Number.isFinite(minDate) && Number.isFinite(maxDate)) {
    const startYear = new Date(minDate).getUTCFullYear();
    const endYear = new Date(maxDate).getUTCFullYear();
    const midYear = Math.round((startYear + endYear) / 2);
    axisLabels = [...new Set([startYear, midYear, endYear].map(String))];
  } else if (maxRelativeSpan > 0) {
    const mid = Math.round(maxRelativeSpan / 2);
    if (mid > 0 && mid < maxRelativeSpan) {
      axisLabels.push(`${mid}d`);
    }
    axisLabels.push(`${maxRelativeSpan}d`);
  }

  return { rows: chartRows, axisLabels };
};
